import React, { useEffect, useState } from "react";
import { Users, UserPlus, Trash2, Loader2, Crown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", emoji: "🛡️", hint: "Full edit + manage team" },
  developer: { label: "Developer", emoji: "💻", hint: "Build & edit the game" },
  artist: { label: "Artist", emoji: "🎨", hint: "Build & edit the game" },
  animator: { label: "Animator", emoji: "🎞️", hint: "Build & edit the game" },
  sound: { label: "Sound", emoji: "🎵", hint: "Build & edit the game" },
  tester: { label: "Tester", emoji: "🧪", hint: "Read-only / play to test" },
  viewer: { label: "Viewer", emoji: "👀", hint: "Read-only access" },
};
const roleLabel = (r) => (ROLE_META[r] ? `${ROLE_META[r].emoji} ${ROLE_META[r].label}` : r);

export default function CollaboratorsDialog({ gameId, open, onClose, isOwner }) {
  const [owner, setOwner] = useState(null);
  const [collabs, setCollabs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [ident, setIdent] = useState("");
  const [inviteRole, setInviteRole] = useState("developer");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/multiplayer/games/${gameId}/collaborators`);
      setOwner(data.owner); setCollabs(data.collaborators || []); setRoles(data.roles || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setIdent(""); setInviteRole("developer"); load(); } }, [open]); // eslint-disable-line

  const add = async () => {
    if (!ident.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/multiplayer/games/${gameId}/collaborators`, { identifier: ident.trim(), role: inviteRole });
      toast.success(`${data.collaborator.name} joined as ${ROLE_META[inviteRole]?.label || inviteRole}! 🤝`);
      setIdent(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const changeRole = async (id, role) => {
    setCollabs((cs) => cs.map((c) => (c.id === id ? { ...c, project_role: role } : c)));
    try { await api.patch(`/multiplayer/games/${gameId}/collaborators/${id}/role`, { role }); toast.success(`Role updated to ${ROLE_META[role]?.label || role}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const remove = async (id) => {
    try { await api.delete(`/multiplayer/games/${gameId}/collaborators/${id}`); toast.success("Collaborator removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="collaborators-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-[hsl(var(--primary))]" /> Collaboration team</DialogTitle>
          <DialogDescription>Invite other creators to co-build this game and pick what each teammate can do.</DialogDescription>
        </DialogHeader>

        {loading ? <div className="py-8 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div> : (
          <div className="space-y-4">
            {isOwner && (
              <div className="space-y-2 rounded-xl border border-[hsl(var(--border))] p-3">
                <Input value={ident} onChange={(e) => setIdent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                  placeholder="Creator's email or username" data-testid="collab-input" />
                <div className="flex items-center gap-2">
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="flex-1" data-testid="collab-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(roles.length ? roles : Object.keys(ROLE_META)).map((r) => (
                        <SelectItem key={r} value={r} data-testid={`collab-role-opt-${r}`}>{roleLabel(r)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={add} disabled={adding} className="rounded-full shrink-0" data-testid="collab-add-btn">
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Invite
                  </Button>
                </div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{ROLE_META[inviteRole]?.hint}</p>
              </div>
            )}
            <div className="space-y-2" data-testid="collab-list">
              {owner && (
                <div className="flex items-center gap-2.5 rounded-xl border border-[hsl(var(--border))] p-2.5">
                  <span className="text-xl">{owner.avatar_emoji}</span>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{owner.name}</div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">Owner · full access</div></div>
                  <Crown className="w-4 h-4 text-amber-400" />
                </div>
              )}
              {collabs.map((c) => (
                <div key={c.id} className="flex items-center gap-2.5 rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`collab-${c.id}`}>
                  <span className="text-xl">{c.avatar_emoji}</span>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">{ROLE_META[c.project_role]?.hint || "collaborator"}</div></div>
                  {isOwner ? (
                    <Select value={c.project_role} onValueChange={(v) => changeRole(c.id, v)}>
                      <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`collab-role-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(roles.length ? roles : Object.keys(ROLE_META)).map((r) => (
                          <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : <span className="text-xs capitalize px-2 py-1 rounded-full bg-[hsl(var(--secondary))]">{roleLabel(c.project_role)}</span>}
                  {isOwner && <button onClick={() => remove(c.id)} data-testid={`collab-remove-${c.id}`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              {collabs.length === 0 && <div className="text-xs text-[hsl(var(--muted-foreground))] text-center py-2">No collaborators yet. Invite someone to build together!</div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
